<template>
  <div class="post-view-page mb-0 d-flex flex-column">

    <b-card
      v-if="post"
      no-body
      class="full-width-bar shadow-none rounded-0 border-0 border-bottom d-flex flex-row justify-content-between align-items-center mb-3 w-100 position-sticky"
      style="top: 0; padding: 1rem 2rem;"
    >
      <div class="d-flex align-items-center">
        <b-button
          variant="link"
          class="p-0 text-body d-flex align-items-center text-decoration-none d-xl-none mr-1"
          @click="goBackToMenu"
        >
          <feather-icon icon="ChevronLeftIcon" size="24" />
        </b-button>

        <h3 class="mb-0 font-weight-bolder">
          {{ post.visitor ? `${post.visitor.firstname} ${post.visitor.lastname}` : $t('postView.unknownVisitor') }}
        </h3>
      </div>

      <div class="d-flex align-items-center">
        <div
          id="tour-track-delete"
          v-b-tooltip="{ title: $t('postView.deleteModal.title'), placement: 'left', trigger: 'hover' }"
          class="d-inline-flex align-items-center p-50 cursor-pointer"
          @click="$bvModal.show('delete-post-modal')"
        >
          <feather-icon icon="Trash2Icon" size="22" class="text-body" />
        </div>
      </div>
    </b-card>

    <div v-if="!loading" class="row flex-grow-1 mx-0">
      <section v-if="post" class="col-12 px-0">

        <div class="d-flex flex-column align-items-start mb-3" style="max-width: 80%;">
          <b-card id="tour-viewing-messages" no-body class="mb-0 border-0 shadow-sm overflow-hidden">
            <div
              v-if="localImageUrl"
              class="p-1"
              style="max-width: 450px;"
            >
              <img
                v-b-tooltip="{ title: $t('postView.expandImage'), placement: 'right', trigger: 'hover' }"
                v-b-modal.image-modal
                :src="localImageUrl"
                class="rounded mw-100 cursor-pointer h-auto"
                style="object-fit: contain; max-height: 450px;"
                :alt="$t('postView.altMessageAttachment')"
              >
            </div>

            <b-card-body class="pt-0 pb-1 px-1">
              <p class="card-text mb-1 text-break">
                {{ post.message }}
              </p>

              <b-button
                id="tour-play-voice"
                v-b-tooltip="{ title: $t('postView.tts'), placement: 'right', trigger: 'hover' }"
                variant="link"
                class="p-0 text-primary d-flex align-items-center text-decoration-none"
                @click="toggleTTS(post.message)"
              >
                <feather-icon :icon="isSpeaking ? 'StopCircleIcon' : 'PlayCircleIcon'" class="font-large-1" />
              </b-button>
            </b-card-body>
          </b-card>

          <div class="d-flex align-items-center small text-body px-1 mt-50">
            <span>{{ post.reviewed_on | formatDateTime }}</span>
            <feather-icon
              id="post-info-icon"
              icon="InfoIcon"
              class="ml-50 cursor-pointer text-body"
            />
            <b-popover target="post-info-icon" triggers="hover focus click" placement="bottomright" custom-class="shadow-sm">
              <div class="small p-25">
                <div class="d-flex align-items-center justify-content-between">
                  <span class="mr-3 text-muted">{{ $t('postView.received') }}</span>
                  <span class="font-weight-bolder">{{ formatPopoverDate(post.reviewed_on) }}</span>
                </div>
              </div>
            </b-popover>
          </div>
        </div>

        <div
          v-for="(reply, index) in replies"
          :id="'tour-track-replies-' + index" :key="reply._id"
          class="d-flex justify-content-end w-100 mt-2 mb-3"
        >
          <div class="d-flex flex-column align-items-end" style="max-width: 80%;">

            <b-card
              :bg-variant="reply.status && reply.status.toLowerCase() === 'rejected' ? 'danger' : 'primary'"
              text-variant="white"
              class="mb-0"
              no-body
              style="max-width: 100%;"
            >
              <b-card-body class="p-1">
                <p
                  class="card-text mb-0 text-break"
                  :class="{ 'text-center mb-1': reply.status && reply.status.toLowerCase() === 'rejected' }"
                >
                  {{ reply.message }}
                </p>
                <div v-if="reply.status && reply.status.toLowerCase() === 'rejected'" class="text-center">
                  <p class="card-text font-weight-bolder small mb-0">{{ $t('postView.rejectionReason') }}</p>
                  <p class="card-text font-italic mb-0 small">{{ reply.rejection_reason }}</p>
                </div>
              </b-card-body>
            </b-card>

            <div class="d-flex justify-content-end align-items-center small text-body w-100 mt-50 px-1">

              <span v-if="reply.status && reply.status.toLowerCase() === 'rejected'" class="d-inline-flex align-items-center text-danger">
                <feather-icon icon="SlashIcon" size="14" class="mr-25" />
                {{ $t('postView.status.replyRejected') }}
              </span>

              <span v-else-if="reply.read_on" class="d-inline-flex align-items-center text-body">
                <span class="d-inline-flex align-items-center mr-25" style="color: #8b8de3;">
                  <feather-icon icon="CheckIcon" size="14" />
                  <feather-icon icon="CheckIcon" size="14" style="margin-left: -10px;" />
                </span>
                {{ reply.read_on | formatDateTime }}
              </span>

              <span v-else-if="reply.status === 'Approved' && !reply.read_on" class="d-inline-flex align-items-center">
                <feather-icon icon="CheckIcon" size="14" class="mr-25" />
                {{ reply.created_at | formatDateTime }}
              </span>

              <span v-else-if="!reply.delivered && (!reply.status || reply.status.toLowerCase() !== 'rejected')" class="d-inline-flex align-items-center">
                <feather-icon icon="ClockIcon" size="14" class="mr-25" />
                {{ reply.created_at | formatDateTime }}
              </span>

              <feather-icon
                :id="'reply-info-' + index"
                icon="InfoIcon"
                size="14"
                class="ml-50 cursor-pointer"
              />

              <b-popover
                :target="'reply-info-' + index"
                triggers="hover focus click"
                placement="bottomleft"
                custom-class="shadow-sm"
              >
                <div class="d-flex flex-column small py-25 px-50">
                  <div class="d-flex align-items-center justify-content-between" :class="{ 'mb-50': reply.read_on }">
                    <span class="mr-4 text-muted">{{ $t('postView.status.sent') }}</span>
                    <span class="font-weight-bolder">{{ formatPopoverDate(reply.created_at) }}</span>
                  </div>
                  <div v-if="reply.read_on" class="d-flex align-items-center justify-content-between">
                    <span class="mr-4 text-muted">{{ $t('postView.status.read') }}</span>
                    <span class="font-weight-bolder">{{ formatPopoverDate(reply.read_on) }}</span>
                  </div>
                </div>
              </b-popover>
            </div>
          </div>
        </div>

      </section>
      <section v-else class="col-12">
        <h4>{{ $t('postView.noPostFound') }}</h4>
      </section>
    </div>

    <section v-else class="flex-grow-1 d-flex justify-content-center align-items-center">
      <b-spinner variant="primary" :label="$t('postView.loadingPost')" />
    </section>

    <div
      v-if="post && post.post_type === 'Digital'"
      id="tour-send-reply"
      class="full-width-bar mt-auto position-sticky"
      style="bottom: 0;"
      :style="loading ? 'pointer-events: none;' : ''"
    >
      <b-card
        v-if="isRestricted && !isOnboarding"
        no-body
        class="shadow-none rounded-0 border-0 border-top mb-0 d-flex flex-row align-items-center px-2 py-1"
      >
        <feather-icon icon="SlashIcon" size="18" class="text-danger mr-1 flex-shrink-0" />
        <span class="small text-body">{{ $t('postView.reply.restricted') }}</span>
      </b-card>
      <reply-send
        v-else
        :post="post"
        :replies="replies"
        class="px-1 py-1"
        @reply-sent="getReplies(true)"
      />
    </div>

    <b-modal id="delete-post-modal" hide-header hide-footer centered size="sm" content-class="custom-delete-modal border-0 shadow-lg" body-class="p-2 text-center">
      <div class="delete-icon-wrapper rounded-circle d-flex align-items-center justify-content-center mx-auto shadow-sm">
        <feather-icon icon="AlertTriangleIcon" size="38" class="text-white" />
      </div>
      <h4 class="font-weight-bolder mb-1 mt-1 text-body">{{ $t('postView.deleteModal.title') }}</h4>
      <p class="text small mb-1">{{ $t('postView.deleteModal.message') }}</p>
      <div class="d-flex justify-content-center w-100 mt-2 mb-1">
        <b-button variant="link" class="text-body font-weight-bold text-decoration-none mr-4 p-0" @click="$bvModal.hide('delete-post-modal')">{{ $t('common.no') }}</b-button>
        <b-button variant="link" class="delete-confirm-btn font-weight-bold text-decoration-none ml-4 p-0" @click="deletePost">{{ $t('common.yes') }}</b-button>
      </div>
    </b-modal>

    <b-modal id="image-modal" hide-header hide-footer centered size="lg">
      <img v-if="localImageUrl" :src="localImageUrl" class="img-fluid w-100 rounded" :alt="$t('postView.altExpandedAttachment')">
    </b-modal>

    <div id="tour-spread-word-anchor" style="position: fixed; top: 50%; left: 50%; width: 0; height: 0;" />
  </div>
</template>

<script>
  import { mapState } from 'vuex';
  import ReplySend from '@/views/postView/sections/postDigitalReply/ReplySend.vue';
  import { TOUR_TARGETS } from '@/services/OnboardingService';
  import PostService from '@/services/PostService';
  import DigitalRepliesService from '@/services/DigitalRepliesService';

  export default {
    name: 'PostView',
    components: {
      ReplySend,
    },
    props: {
      id: {
        required: true,
        type: String,
      },
    },
    data() {
      return {
        loading: true,
        loadingVisitor: true,
        loadingPrison: true,
        loadingMonitor: true,
        post: null,
        visitor: null,
        replies: [],
        polling: null,
        isSpeaking: false,
        localImageUrl: null,
        dummyPost: {
          _id: 'onboarding-post-1',
          post_type: 'Digital',
          message: this.$t('onboarding.onboardingExamplePost'),
          reviewed_on: new Date().toISOString(),
          visitor: { firstname: 'Joe', lastname: 'Bloggs' },
          // eslint-disable-next-line global-require
          image_url: require('@/assets/images/dummy_post_image.png'),
        },
        dummyReplies: [
          {
            _id: 'onboarding-reply-1',
            status: 'Approved',
            message: this.$t('onboarding.onboardingExampleReply'),
            created_at: new Date().toISOString(),
            read_on: null,
          },
        ],
      };
    },
    computed: {
      ...mapState({
        isRestricted: state => state.restriction.isRestricted,
      }),
      isOnboarding() {
        return this.$store.state.app.isOnboarding;
      },
    },
    watch: {
      '$route.params.id': {
        immediate: true,
        handler(newId) {
          if (newId) {
            // If TTS is active, ensure it is stopped
            this.stopTTS();
            this.stopPolling();
            this.getPost();
          }
        },
      },
    },
    mounted() {
      this.getPost();
      this.$store.dispatch('restriction/fetchRestrictionStatus');
      if (this.isOnboarding) {
        this.$root.$emit('tour-resume', TOUR_TARGETS.VIEWING_MESSAGES);
      }
    },
    beforeDestroy() {
      this.stopPolling();
      this.stopTTS();

      if (this.localImageUrl && this.localImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.localImageUrl);
      }
    },
    methods: {
      async getPost() {
        this.loading = true;
        const currentId = this.id;

        if (this.isOnboarding) {
          this.post = this.dummyPost;
          await Promise.all([
            this.getReplies(true),
            this.preloadImage(this.post.image_url),
          ]);
          this.loading = false;
          return;
        }
        try {
          const res = await PostService.getPost(this.id);
          if (this.id !== currentId) return; // Abort if post changed
          this.post = res.data;
          this.startPolling();
          this.$root.$emit('refresh-post-list');

          await Promise.all([
            this.getReplies(true),
            this.preloadImage(this.post.image_url),
          ]);
        } catch (err) {
          if (this.id !== currentId) return;
          console.error('could not get post', err);
          this.$toast.error(this.$t('postView.error.fetchPost'), {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        } finally {
          if (this.id === currentId) {
            this.loading = false;
          }
        }
      },
      async getReplies(isSilent = false) {
        // eslint-disable-next-line no-underscore-dangle
        if (!this.post || !this.post._id) return;
        if (!isSilent) this.loading = true;
        // eslint-disable-next-line no-underscore-dangle
        const targetPostId = this.post._id;
        if (this.isOnboarding) {
          this.replies = this.dummyReplies;
          this.loading = false;
          return;
        }

        try {
          // eslint-disable-next-line no-underscore-dangle
          const res = await DigitalRepliesService.getRepliesByPostId(this.post._id);
          if (this.id !== targetPostId) return; // Abort if post changed
          this.replies = res.data;
        } catch (error) {
          if (this.id !== targetPostId) return;
          if (!isSilent) {
            this.$toast.error(this.$t('postView.error.fetchReplies'), {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          }
        } finally {
          if (!isSilent && this.id === targetPostId) {
            this.loading = false;
          }
        }
      },
      startPolling() {
        if (this.polling) return;

        this.polling = setInterval(() => {
          // eslint-disable-next-line no-underscore-dangle
          if (this.post && this.post._id) {
            this.getReplies(true);
          }
        }, 60000); // 60,000ms = 1 minute
      },
      stopPolling() {
        if (this.polling) {
          clearInterval(this.polling);
          this.polling = null;
        }
      },
      async preloadImage(src) {
        if (!src) {
          this.localImageUrl = null;
          return;
        }
        if (this.isOnboarding) {
          this.localImageUrl = src;
          return;
        }
        try {
          const response = await fetch(src);
          if (!response.ok) {
            console.error('Could not fetch post image');
          }
          const blob = await response.blob();
          this.localImageUrl = URL.createObjectURL(blob);
        } catch (error) {
          this.localImageUrl = src;
        }
      },
      goBackToMenu() {
        this.stopTTS();
        this.stopPolling();
        this.$root.$emit('toggle-mobile-menu', true);
      },
      deletePost() {
        this.$bvModal.hide('delete-post-modal');
        // eslint-disable-next-line no-underscore-dangle
        PostService.deletePost(this.post._id).then(() => {
          this.$toast.success(this.$t('postView.deleteSuccess'), {
            toastClassName: ['toast-std', 'success-toast'],
          });
          this.$root.$emit('refresh-post-list');
          if (this.$route.name !== 'dashboard') {
            this.$router.push({ name: 'dashboard' });
          }
        }).catch(err => {
          console.error('Error deleting post:', err);
          this.$toast.error(this.$t('postView.deleteFailed'), {
            toastClassName: ['toast-std', 'warning-toast'],
          });
        });
      },
      toggleTTS(text) {
        const synth = window.speechSynthesis;
        // If already speaking, stop it
        if (this.isSpeaking) {
          synth.cancel();
          this.isSpeaking = false;
          return;
        }

        if (text) {
          const utterance = new SpeechSynthesisUtterance(text);
          utterance.onend = () => {
            this.isSpeaking = false;
          };
          utterance.onerror = () => {
            this.isSpeaking = false;
          };

          this.isSpeaking = true;
          synth.speak(utterance);
        }
      },
      stopTTS() {
        window.speechSynthesis.cancel();
        this.isSpeaking = false;
      },
      formatPopoverDate(dateVal) {
        if (!dateVal) return '';

        const date = new Date(dateVal);
        const today = new Date();
        const isToday = date.toDateString() === today.toDateString();

        // Grab the output from your existing global filter
        const baseFormat = this.$options.filters.formatDateTime(dateVal);

        if (isToday) {
          return baseFormat;
        }

        const currentLocale = this.$i18n.locale;

        const timeString = date.toLocaleTimeString(currentLocale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        });

        return this.$t('time.dateAtTime', {
          date: baseFormat,
          time: timeString,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
.post-view-page {
  position: relative;
  margin: -2rem;
  padding: 0 2rem;
  min-height: 100vh;
  background-color: #F0EEF5;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url('~@/assets/images/doodle_background.png');
    background-repeat: repeat;
    background-attachment: fixed;
    z-index: 0;
    background-size: 800px;
    opacity: 0.1;
    filter: grayscale(100%);
  }

  > * {
    position: relative;
    z-index: 1;
  }
}

.full-width-bar {
  margin-left: -2rem;
  margin-right: -2rem;
  width: calc(100% + 4rem) !important;
  z-index: 50;
}

.delete-icon-wrapper {
  width: 80px;
  height: 80px;
  background-color: #f02d65;
  margin-top: -4.5rem;
  z-index: 10;
}

.delete-confirm-btn {
  color: #f02d65 !important;
  &:hover {
    color: #d12255 !important;
  }
}

</style>
<style lang="scss">
#delete-post-modal .modal-content {
  overflow: visible !important;
}

.dark-layout .post-view-page {
  background-color: #202029;

  &::before {
    opacity: 0.1;
    filter: none;
  }
}

/* Light Mode */
.popover {
  .popover-body {
    border: 0 !important;
    border-radius: 0.358rem !important;
  }

  &.bs-popover-top .arrow::after { border-top-color: #ffffff !important; }
  &.bs-popover-right .arrow::after { border-right-color: #ffffff !important; }
  &.bs-popover-bottom .arrow::after { border-bottom-color: #ffffff !important; }
  &.bs-popover-left .arrow::after { border-left-color: #ffffff !important; }
}

/* Dark Mode Override */
.dark-layout .popover {
  background-color: #202029 !important;
  border-color: #3b3b48 !important;

  .popover-body {
    background-color: #202029 !important;
    border: 0 !important;
    border-radius: 0.358rem !important;
  }

  &.bs-popover-top .arrow::before { border-top-color: #3b3b48 !important; }
  &.bs-popover-right .arrow::before { border-right-color: #3b3b48 !important; }
  &.bs-popover-bottom .arrow::before { border-bottom-color: #3b3b48 !important; }
  &.bs-popover-left .arrow::before { border-left-color: #3b3b48 !important; }

  &.bs-popover-top .arrow::after { border-top-color: #202029 !important; }
  &.bs-popover-right .arrow::after { border-right-color: #202029 !important; }
  &.bs-popover-bottom .arrow::after { border-bottom-color: #202029 !important; }
  &.bs-popover-left .arrow::after { border-left-color: #202029 !important; }
}
</style>
